import { take, fork, cancelled, spawn, cancel, call, cps, race, all, CallEffect } from 'redux-saga/effects';
import { delay, Task } from 'redux-saga';
import { runInAction } from 'mobx';
import uuid from 'uuid/v4';
import { Channel } from '../Shared/utils/user-action-channel';
import { WebGivingUserAction } from './webgiving-user-actions';
import { IWebGivingMainViewModel, reportError } from './main/webgiving-main-view-model';
import { getWebGivingDataService, setNewCRSFoken, WebGivingApiConfigActions, WebGivingActionRequest, WebGivingActionResponse } from './webgiving-data-service';
import { WebGivingPage, WebGivingPageViewModel } from './pages';
import { PostError } from '../Shared/utils/ajax-client';
import { GoogleAnalyticsWrapper } from './infrastructure/google-analytics-wrapper';
import {
	WebGivingPaymentResultStatus,
	WebGivingApiConfig,
	ConfirmPaymentResponse,
	MerchantConflictResponse,
	PaymentMethodType,
	WebGivingViewModel,
	ResetPageReason,
	WebGivingPersonModel,
	Country,
	Currency,
	WebGivingPaymentMethodModel,
	WebGivingPreconfiguredPayment,
	PageStatsRequest,
	FrequencyCode,
	RecurringSuggestionQueryResult,
	RecurringSuggestionModel,
	UpdateFastModeEnabledErrorCode,
	ThankYouAnimationType,
	ApplePayValidateMerchantResponse,
	ConfirmPaymentRequest,
	WebGivingLogInfoRequest,
	WebGivingLogInfoType,
	ConfirmationCodeErrorResponse,
	SplitLineItem,
	WebGivingMerchant,
} from './webgiving-generated';
import {
	paymentStateToPaymentRequest,
	defaultPaymentState,
	GiftType,
	clearInvalidCustomFields,
	IWebGivingOneTimePaymentState,
	generateOneTimeGiftDateLabel,
	IWebGivingRecurringPaymentState,
	IWebGivingCommonPaymentState,
} from './webgiving-payment-state';
import { IWebGivingAppState, IWebGivingMobileNumberRequest, defaultAppState, userShouldReceiveWelcomeSMS } from './webgiving-app-state';
import { NotificationViewModel, createEmailVerificationNotification } from './components/notification/notification';
import { PageCreateAccountViewModel } from './pages/page-create-account/page-create-account-view-model';
import { PageEnterMobileNumberViewModel } from './pages/page-enter-mobile-number/page-enter-mobile-number';
import { PageConfirmBankAccountViewModel } from './pages/page-confirm-bank-account/page-confirm-bank-account';
import { confirmationCodeSentMessage } from './helpers/notification-message-helper';
import { addQueryStringParameter } from '../Shared/utils/url-utils';
import { ActionHandlers, AsyncActionHandler, ActionConcurrencyInstruction, callInAction, ActionConfig } from '../Shared/utils/saga-utils';
import { IPagePaymentSuccessData } from './pages/page-payment-success/page-payment-success-view-model';
import { merchantConfigurationChangedError, WebGivingErrorType } from './components/error-message/error-message';
import { PaymentMethodApplePayParams } from './pages/page-payment-method/page-payment-method-view-model';
import { ModalType } from './modals/modal-type';
import { scrollErrorIntoViewOnNextFrame } from './utils/error-focuser';
import { setUserRememberedAmount, clearUserRemembered } from './utils/local-storage';
import { convertLocalDateToUtcForLogs } from '../Shared/utils/date-helper';
import { handleSetRecurringSuggestion, configureRecurringSuggestionInformation, handleSetRecurringSuggestionSuccess } from './utils/recurring-suggestion-util';
import { ConfirmGiftPostError, defaultErrorMessage } from './utils/error-utils';
import { getSelectedFundKey, getSelectedFundName, getFundName } from './utils/reference-field-helper';
import { isAfterToday } from './utils/date-utils';
import { getLanguageWithFallback } from './utils/language-utils';
import { translationLogging } from './translation';
import { ApplePayMerchantCapabilities, ApplePaySupportedNetworks, getApplePayNetworksAndCapabilities, isApplePayCancelError, mapFrequencyCodeToApplePayInterval } from './utils/applepay-utils';
import { getLineItemsAmountWithFees, splitLineItemSuccessStatuses, constructSplitLineItemResults} from './utils/webgiving-utils';

export interface IWebGivingSagaContext {
	readonly userActionChannel: Channel<WebGivingUserAction>;
	readonly mainViewModel: IWebGivingMainViewModel;
	readonly dataService: WebGivingSagaDataService;
	readonly disableValidation?: boolean;
	executingBlockingAction?: boolean;
	appState: IWebGivingAppState;
	reportError: (error: any, customData?: any) => void;
}

const digitsOnly = /\D/g;
const extendedTimeout = 65000;
const extendedResponseTime = 30000;
const applePayResponseTime = 29000;
let recurringSuggestionRequest: Task;
const applePaySdkVersion = 1;



export type WebGivingSagaDataService = {
	[x in keyof WebGivingApiConfigActions]: (data:{ request: WebGivingActionRequest<x>, queryStringParameters?: { [key: string]: string }, timeout?: number}) => Iterator<WebGivingActionResponse<x>>
};

export function sagaDataService(getAppState: () => IWebGivingAppState): WebGivingSagaDataService {
	return (Object.keys(WebGivingApiConfig.actions) as Array<keyof WebGivingApiConfigActions>).reduce((acc: any, actionKey: keyof WebGivingApiConfigActions) => {
		acc[actionKey] = function* (data:{request: any, queryStringParameters?: { [key: string]: string }, timeout?: number}) {
			const action: (...args: any[]) => PromiseLike<any> = getWebGivingDataService()[actionKey];
			return yield action(data, getAppState);
		};

		return acc;
	}, {});
}

declare const webGivingActionResponse: { [x in keyof WebGivingApiConfigActions]: WebGivingActionResponse<x> };

export function createActionHandlers() {

	const actionHandlers = new ActionHandlers<IWebGivingSagaContext, typeof WebGivingUserAction[keyof typeof WebGivingUserAction]>();

	/**
	 * We need to handle actions in a non-blocking way (redux-saga/effects/fork) and cancel or block actions when it suits our business rules.
	 * Luckily, our business rules are rather simple ATM.
	 * - SignOut cancells everything and blocks everything
	 * - SubmitCurrentPage cancells everything and blocks everything except for SignOut
	 * - Resend security code does not block anything
	 * - All other actions are not async, hence they cannot be cancelled, but can be blocked by SignOut or SubmitCurrentPage
	 */

	// there're multiple actions that eventually call handleSubmitCurrentPageSaga
	// let's have a reusable config for them
	const submitCurrentPageConfig: Partial<ActionConfig> = {
		blocking: true,
		priority: 10,
	};

	actionHandlers.add(
		WebGivingUserAction.SelectInitialPage, {
			...submitCurrentPageConfig,
			async: true,
			handler: (context) => handleInitialPageSelection(context),
		});
	actionHandlers.add(
		WebGivingUserAction.VerifyApplePaySDK, {
			async: true,
			handler: (context) => verifyApplePaySDK(context),
		});
	actionHandlers.add(
		WebGivingUserAction.SubmitCurrentPage, {
			...submitCurrentPageConfig,
			async: true,
			handler: (context) => handleSubmitCurrentPageSaga(context),
		});
	actionHandlers.add(
		WebGivingUserAction.SignOut, {
			async: true,
			blocking: true,
			priority: 100,
			handler: (context) => handleSignOut(context),
		});
	actionHandlers.add(
		WebGivingUserAction.PreviousPage, {
			async: false,
			handler: (context) => handlePreviousPageSaga(context),
		});
	actionHandlers.add(
		WebGivingUserAction.RequestConfirmationCode, {
			...submitCurrentPageConfig,
			async: true,
			handler: (context, action) => requestConfirmationCode(context, action.useVoiceCall),
		});
	actionHandlers.add(
		WebGivingUserAction.RequestResendOfConfirmationCode, {
			async: true,
			handler: (context, action) => requestResendOfConfirmationCode(context, action.useVoiceCall),
		});
	actionHandlers.add(
		WebGivingUserAction.OpenModalDialog, {
			async: false,
			handler: (context, action) => handleOpenModal(context, action.modalType),
		});
	actionHandlers.add(
		WebGivingUserAction.LogCryptoEngagement, {
			async: true,
			handler: (context, action) => logInfo(context, {Type: WebGivingLogInfoType.CryptoEngagement}),
		});
	actionHandlers.add(
		WebGivingUserAction.LogCryptoGiving, {
		async: true,
			handler: (context, action) => logInfo(context, {Type: WebGivingLogInfoType.CryptoGiving}),
		});
	actionHandlers.add(
		WebGivingUserAction.LogStockEngagement, {
		async: true,
			handler: (context, action) => logInfo(context, {Type: WebGivingLogInfoType.StockEngagement}),
		});
	actionHandlers.add(
		WebGivingUserAction.LogStockGiving, {
		async: true,
			handler: (context, action) => logInfo(context, {Type: WebGivingLogInfoType.StockGiving}),
		});
	actionHandlers.add(
		WebGivingUserAction.ChangeBankDetails, {
			async: false,
			handler: (context) => handleChangeBankAccountDetails(context),
		});
	actionHandlers.add(
		WebGivingUserAction.ChangePaymentDetails, {
			async: true,
			handler: (context) => handleChangePaymentDetails(context),
		});
	actionHandlers.add(
		WebGivingUserAction.ChangeMobileNumber, {
			async: false,
			handler: (context) => handleChangeMobileNumber(context),
		});
	actionHandlers.add(
		WebGivingUserAction.NewPaymentMethod, {
			async: false,
			handler: (context) => handleNewPaymentMethod(context),
		});
	actionHandlers.add(
		WebGivingUserAction.ResetPageBecauseOfError, {
			async: false,
			handler: (context, action) => handleResetPageBecauseOfError(action.reason, context),
		});
	actionHandlers.add(
		WebGivingUserAction.BackToConfirmGift, {
			async: false,
			handler: (context) => navigateToConfirmGiftPage(context),
		});
	actionHandlers.add(
		WebGivingUserAction.ChangePayerCoversFees, {
			async: false,
			handler: (context, action) => handleChangePayerCoversFees(context, action.payerCoversFees),
		});
	actionHandlers.add(
		WebGivingUserAction.RecordPageStats, {
			async: false,
			handler: (context, action) => handleRecordPageStats(context, action.returnUrl),
		});
	actionHandlers.add(
		WebGivingUserAction.GiveAgain, {
			async: false,
			handler: (context, action) => handleGiveAgain(context),
		});

	actionHandlers.add(
		WebGivingUserAction.SetRecurringSuggestion, {
			async: false,
			handler: (context) => handleSetRecurringSuggestion(context, postStatsData),
		});
	actionHandlers.add(
		WebGivingUserAction.AnimateThankYou, {
			async: false,
			handler: (context, action) => handleConfettiAnimation(context, action.animationType),
		});

	actionHandlers.add(
		WebGivingUserAction.RecordRecurringSuggestionView, {
			async: true,
			handler: (context) => handleRecordRecurringSuggestion(context),
		});

	actionHandlers.add(
		WebGivingUserAction.FetchAppContentCard, {
			async: true,
			handler: context => handleAppCardContent(context),
		});

	actionHandlers.add(
		WebGivingUserAction.UpdateFastModeEnabled, {
			async: true,
			handler: (context, action) => handleUpdateFastModeEnabled(context, action.mode),
		});
	actionHandlers.add(
		WebGivingUserAction.MakeApplePay, {
			async: true,
			handler: (context, action) => makeApplePaymentSaga(context, action.amountWithFees),
		});

	return actionHandlers;
}

function getUserActionName(userAction: WebGivingUserAction): string | undefined {
	return (Object.getOwnPropertyNames(WebGivingUserAction) as Array<keyof typeof WebGivingUserAction>)
		.filter((x: keyof typeof WebGivingUserAction) => userAction instanceof WebGivingUserAction[x])[0];
}

export function* webGivingSaga(context: IWebGivingSagaContext): IterableIterator<any> {
	const { userActionChannel } = context;
	const actionHandlers = createActionHandlers();

	let currentAsyncHandler: AsyncActionHandler<IWebGivingSagaContext> | undefined;
	let currentAsyncTask: Task | undefined;

	while (true) {
		const userAction: WebGivingUserAction = yield take(userActionChannel);
		try {
			const incomingActionHandler = actionHandlers.get(userAction);

			if (currentAsyncHandler && currentAsyncTask && currentAsyncTask.isRunning()) {
				const instruction = actionHandlers.getInstructionForIncomingAction(currentAsyncHandler, incomingActionHandler);

				switch (instruction) {
					case ActionConcurrencyInstruction.AllowIncoming:
						console.log('Allowing incoming action', { running: currentAsyncHandler.actionType.name, incoming: incomingActionHandler.actionType.name });
						break;
					case ActionConcurrencyInstruction.CancelRunning:
						console.log('Cancelling running action', { running: currentAsyncHandler.actionType.name, incoming: incomingActionHandler.actionType.name });
						if (currentAsyncHandler.blocking) {
							context.executingBlockingAction = false;
						}
						currentAsyncTask.cancel();
						currentAsyncHandler = undefined;
						break;
					case ActionConcurrencyInstruction.BlockIncoming:
						console.log('Blocking incoming action', { running: currentAsyncHandler.actionType.name, incoming: incomingActionHandler.actionType.name });
						continue;
					default:
						throw new Error(`ActionConcurrencyInstruction[${instruction}] is not supported`);
				}
			}

			console.log('Starting action', { incoming: incomingActionHandler.actionType.name });
			if (incomingActionHandler.async) {
				currentAsyncHandler = incomingActionHandler;

				if (currentAsyncHandler.blocking) {
					context.executingBlockingAction = true;
				}

				currentAsyncTask = yield fork(function* () {
					try {
						yield callInAction(incomingActionHandler.handler, context, userAction);
					} catch (ex) {
						context.reportError(ex, {
							sagaRunningFork: true,
							sagaUserAction: getUserActionName(userAction),
						});
					} finally {
						if (!(yield cancelled())) {
							context.executingBlockingAction = false;
						}
					}
				});
			} else {
				runInAction(() => incomingActionHandler.handler(context, userAction));
			}
		} catch (ex) {
			context.reportError(ex, {
				sagaUserAction: getUserActionName(userAction),
			});
		} finally {
			if (context.mainViewModel) {
				const { currentPageViewModel } = context.mainViewModel;
				const page = (currentPageViewModel && WebGivingPage[currentPageViewModel.type]) || "";
				translationLogging.setPageTitle(`${page}`);
			}
		}
	}
}

function* handleInitialPageSelection(context: IWebGivingSagaContext): IterableIterator<any> {
	const { mainViewModel, appState: { paymentState, person } } = context;
	const { viewData, uiConfiguration, openPage, setCurrentPageShouldBeVisible } = mainViewModel;

	if (uiConfiguration.SkipGivePage && person) {
		navigateToConfirmGiftPage(context);
	} else {
		const page = new WebGivingPageViewModel.Give(paymentState, viewData.MerchantInfo, viewData.BrandingPackage);
		if (uiConfiguration.AttemptGivePageSkip && page.validate().isValid) {
			yield callInAction(navigatePastGivePage, context, page);
		} else {
			openPage(page);
		}
	}

	setCurrentPageShouldBeVisible();
}

function* makeApplePaymentSaga(context: IWebGivingSagaContext, amountWithFees?: number): IterableIterator<any>  {
	const { mainViewModel, appState: { paymentState } } = context;
	const { currentPageViewModel, viewData } = mainViewModel;
	ensurePerson(context.appState.person);

	const hasLineItems = paymentState.LineItems && paymentState.LineItems.length > 1;
	updatePaymentState(context, {
		fundName: getSelectedFundName(paymentState.referenceFieldValues, viewData.MerchantInfo.CustomFields),
		fundKey: getSelectedFundKey(paymentState.referenceFieldValues, viewData.MerchantInfo.CustomFields),
		payerElectedToCoverFees: paymentState.payerElectedToCoverFees,
		paymentMethodId: null,
		calculatedAmountWithFees: amountWithFees || paymentState.calculatedAmountWithFees,
		LineItems: hasLineItems
			? getLineItemsAmountWithFees(paymentState.LineItems, viewData.MerchantInfo.TransactionFeeRate, paymentState.payerElectedToCoverFees)
			: undefined
	});

	const paymentRequest = getApplePayPaymentRequest(context);

	if (!paymentRequest) {
		// TODO: Properly handle Failure mode: https://pushpay.atlassian.net/browse/PP-51119
		currentPageViewModel.error = {
			errorType: WebGivingErrorType.Generic,
			message: defaultErrorMessage,
		};
		return;
	}

	var hasError = false;
	try {
		currentPageViewModel.isLoading = true;
		GoogleAnalyticsWrapper.sendEvent('ApplePay', 'MakeApplePayment', 'initiated', { SourcePageName: currentPageViewModel && WebGivingPage[currentPageViewModel.type] });

		const session = new ApplePaySession(applePaySdkVersion, paymentRequest);

		session.onpaymentmethodselected = () => {
			session.completePaymentMethodSelection(
				paymentRequest.total,
				paymentRequest.lineItems as ApplePayJS.ApplePayLineItem[]
			);
		};

		session.onshippingcontactselected = () => {
			session.completeShippingContactSelection(
				ApplePaySession.STATUS_SUCCESS,
				paymentRequest.shippingMethods || [],
				paymentRequest.total, paymentRequest.lineItems as ApplePayJS.ApplePayLineItem[]
			);
		};

		session.onshippingmethodselected = () => {
			session.completeShippingMethodSelection(
				ApplePaySession.STATUS_SUCCESS,
				paymentRequest.total,
				paymentRequest.lineItems as ApplePayJS.ApplePayLineItem[]
			);
		};

		const validationAndAuthorizeTasks = all([
			call(validateApplePayMerchant, context, session),
			call(authorizeApplePay, context, session)
		]);

		const cancellationTask = call(cancelApplePaySession, session, context);

		yield spawn(logInfo, context, { Type: WebGivingLogInfoType.ApplePaySheetOpening, SourcePageName: currentPageViewModel && WebGivingPage[currentPageViewModel.type] });

		session.begin();

		const result = yield race({
			completed: validationAndAuthorizeTasks,
			cancelled: cancellationTask,
		});

		const { completed: [validated, payment] } = result as unknown as { completed: [boolean, { response: ConfirmPaymentResponse | ConfirmationCodeErrorResponse, timedout: boolean }] };

		let analyticsResult = 'failed';

		if (validated && payment.response) {
			analyticsResult = payment.timedout ? 'timedout and completed' : 'completed';

			if (currentPageViewModel.type === WebGivingPage.ConfirmMobileNumberForPayment && payment.response.ConfirmationCodeError ) {
				session.abort();
				yield call(handleConfirmationCodeError, currentPageViewModel, payment.response);
			} else {
				yield callInAction(handleConfirmPaymentResponse, context, payment.response as ConfirmPaymentResponse, session);
			}
		} else {
			session.abort();
		}

		GoogleAnalyticsWrapper.sendEvent('ApplePay', 'MakeApplePayment', analyticsResult, { SourcePageName: currentPageViewModel && WebGivingPage[currentPageViewModel.type] });
	} catch (error) {

		if (isApplePayCancelError(error)) {
			// User opted to cancel the sheet;
			return;
		}

		GoogleAnalyticsWrapper.sendEvent('ApplePay', 'MakeApplePayment', 'errored', {
			Error: error || '',
			SourcePageName: currentPageViewModel && WebGivingPage[currentPageViewModel.type]
		});

		if (error instanceof PostError) {
			if (error.conflict) {
				const updateData: MerchantConflictResponse = error.updatedRecordForConflict;
				updateMerchantInfo(context, updateData);
				currentPageViewModel.error = merchantConfigurationChangedError;
			} else {
				if (!error.shouldReloadPage) {
					throw new ConfirmGiftPostError('ConfirmGiftPostError', viewData.MerchantInfo.PaymentLabel.NounLowerCase);
				}
				throw error;
			}
		} else {
			currentPageViewModel.error = {
				errorType: WebGivingErrorType.Generic,
				message: defaultErrorMessage,
			};
			hasError = true;
		}
	} finally {
		currentPageViewModel.isLoading = false;
		yield spawn(logInfo, context, {
			Type: hasError ? WebGivingLogInfoType.ApplePaySheetClosedWithError : WebGivingLogInfoType.ApplePaySheetClosed,
			SourcePageName: currentPageViewModel && WebGivingPage[currentPageViewModel.type]
		})
	}
}

function* authorizeApplePay(context: IWebGivingSagaContext, session: ApplePaySession) {
	const { appState, dataService, mainViewModel } = context;
	const { paymentState } = appState;
	const { currentPageViewModel, viewData } = mainViewModel;

	var event: ApplePayJS.ApplePayPaymentAuthorizedEvent = yield cps(cb => {
		session.onpaymentauthorized = (event) => {
			cb(null, event);
		};
	});

	const model = getPaymentStateToConfirmPaymentRequest(context, event.payment);

	try {
		let responseTask: CallEffect;

		if (currentPageViewModel.type === WebGivingPage.ConfirmMobileNumberForPayment) {
			const mobileNumberRequest = ensureMobileNumberRequest(appState.mobileNumberRequest);

			responseTask = call(dataService.verifyPaymentConfirmationCode, {
				request: {
					model: {
						Code: currentPageViewModel.securityCode.value,
						PaymentToken: paymentState.paymentToConfirmToken as any,
						PaymentTokens: null,
						RequestToken: mobileNumberRequest.mobileRequestToken,
						PayerEnabledAnniversaryReminder: model.PayerElectedToCoverFees,
						RememberMe: currentPageViewModel.rememberMobileNumber.value,
						ShouldSendWelcomeSms: userShouldReceiveWelcomeSMS(appState, viewData),
						RecurringSuggestionId: appState.recurringSuggestionId,
						EmailAppContentCardModel: model.EmailAppContentCardModel,
						DisplayedInWebGivingAppContentCardType: model.DisplayedInWebGivingAppContentCardType,
						Language: model.Language,
						InitialUrlParameters: model.InitialUrlParameters,
						ApplePayInfo: model.ApplePayInfo
					}
				}, timeout: extendedTimeout
			});
		} else {
			responseTask = call(dataService.confirmPayment, { request: { model }, timeout: extendedTimeout, queryStringParameters: { unique: uuid() } });
		}

		const { timedout, response }: { timedout: boolean, response: ConfirmPaymentResponse } = yield race({
			timedout: call(applePayResponseTimer),
			response: responseTask,
		});

		if (timedout) {
			// Custom delayed response as the onpaymentauthorized function must complete the payment and respond before the 30 second timeout
			// PP-52418 - https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778020-onpaymentauthorized

			return {
				response: {
					Status: WebGivingPaymentResultStatus.Delayed,
					ResultDescription: '',
					PaymentToken: paymentState.paymentToConfirmToken as any,
					RequestToken: '',
					MobileNumber: appState.mobileNumberRequest,
					ConfirmedMobileToken: '',
					SuccessThankYouAnimationType: ThankYouAnimationType.None,
					ReceiptHasBeenSent: false,
					WelcomeSMSHasBeenSent: false,
					FastModeAvailable: false,
					FastModeCurrentlyEnabled: false,
					ConfirmationCodeError: false,
					ConfirmationReason: 0,
					RedirectUrl: '',
					AppConnectData: null,
					UpdatedMerchantDetails: null,
					UpdatedPersonPaymentMethods: null,
					SuccessThankYouMessage: null
				}, timedout
			};
		}

		return { response, timedout };

	} catch (error) {
		session.abort();
		throw error;
	}
}

function* applePayResponseTimer() {
	yield delay(applePayResponseTime);
	return true;
}

function* validateApplePayMerchant(context: IWebGivingSagaContext, session: ApplePaySession) {
	const event: ApplePayJS.ApplePayValidateMerchantEvent = yield cps((cb) => {
		session.onvalidatemerchant = (event) => {
			cb(null, event);
		};
	});

	try {
		const result: ApplePayValidateMerchantResponse = yield context.dataService.validateApplePayMerchant(
			{
				request: {
					request: {
						Uri: event.validationURL
					}
				}, timeout: extendedTimeout
			});

		if (result.Success && Boolean(result.Result)) {
			session.completeMerchantValidation(JSON.parse(result.Result));
			return result.Success;
		} else{
			throw new Error("Apple Pay Merchant validation failed");
		}
	} catch (error) {
		session.abort();
		throw error;
	}
}

function* cancelApplePaySession(session: ApplePaySession, context: IWebGivingSagaContext) {
	yield cps((cb) => {
		session.oncancel = (event) => {
			cb(event, null);
		};
	});

	const { currentPageViewModel } = context.mainViewModel;
	GoogleAnalyticsWrapper.sendEvent('ApplePay', 'MakeApplePayment', 'session cancelled', { SourcePageName: currentPageViewModel && WebGivingPage[currentPageViewModel.type] });
}

function handleOpenModal(context: IWebGivingSagaContext, modalType: ModalType) {
	context.mainViewModel.openModal(modalType);
}

function handleNewPaymentMethod(context: IWebGivingSagaContext) {
	const { mainViewModel, appState } = context;
	const { uiConfiguration } = mainViewModel;
	const person = ensurePerson(appState.person);

	mainViewModel.openPage(new WebGivingPageViewModel.PaymentMethod(
		mainViewModel.viewData.MerchantInfo,
		person,
		personHasPaymentMethods(context) ? null : getApplePayParams(context),
		Boolean(uiConfiguration.HideACH),
	));
}

function handleChangeBankAccountDetails(context: IWebGivingSagaContext) {
	const { mainViewModel, appState } = context;
	const { uiConfiguration, currentPageViewModel } = mainViewModel;
	const { achModel } = currentPageViewModel as PageConfirmBankAccountViewModel;
	const person = ensurePerson(appState.person);

	mainViewModel.openPage(new WebGivingPageViewModel.PaymentMethod(
		mainViewModel.viewData.MerchantInfo,
		person,
		null,
		Boolean(uiConfiguration.HideACH),
		PaymentMethodType.ACH,
		achModel,
	));
}

function handleChangeMobileNumber(context: IWebGivingSagaContext) {
	const { mainViewModel, appState } = context;
	const { viewData: { PreconfiguredPaymentDetails } } = mainViewModel;

	appState.mobileNumberRequest = null;
	if (PreconfiguredPaymentDetails !== null) {
		PreconfiguredPaymentDetails.MobileNumber = '';
	}

	mainViewModel.openPage(populateEnterMobileNumberPageData(context));
}

function* handleChangePaymentDetails(context: IWebGivingSagaContext): IterableIterator<any> {
	const { mainViewModel, appState: { paymentState } } = context;
	yield cancelRunningRecurringSuggestionRequest(context);
	mainViewModel.openPage(new WebGivingPageViewModel.Give(paymentState, mainViewModel.viewData.MerchantInfo, mainViewModel.viewData.BrandingPackage));
}

function handleConfettiAnimation(context: IWebGivingSagaContext, animationType: ThankYouAnimationType) {
	const { mainViewModel } = context;
	try {
		mainViewModel.showAnimation(animationType);
	} catch (error) {
		// We don't want to display an error.
	}
}

function* verifyApplePaySDK(context: IWebGivingSagaContext): IterableIterator<any> {
	const {
		viewData: { ApplePayMerchantIdentifier, MerchantInfo },
		setApplePayAvailable,
		setAppLoading,
		uiConfiguration,
		currentPageViewModel
	} = context.mainViewModel;

	if (uiConfiguration.HideApplePay || !MerchantInfo.SupportsApplePay) {
		return;
	}

	let loadingInitiated = false;

	if (currentPageViewModel && [WebGivingPage.PaymentMethod, WebGivingPage.ConfirmGift].includes(currentPageViewModel.type)) {
		setAppLoading(true);
		loadingInitiated = true;
	}

	const applePaySDK: any = (window as any).ApplePaySession;
	const netWorksAndCapabilities = getApplePayNetworksAndCapabilities(MerchantInfo);
	let hasApplepay = false;
	let hasApplepayCards = false;

	if (netWorksAndCapabilities && applePaySDK !== undefined) {
		try {
			hasApplepayCards = Boolean(yield call(
				applePaySDK.canMakePaymentsWithActiveCard,
				ApplePayMerchantIdentifier
			));
			hasApplepay = Boolean(yield call(
				applePaySDK.canMakePayments,
				ApplePayMerchantIdentifier
			));
		} catch (error) {
			if (loadingInitiated){
				setAppLoading(false);
			}
			hasApplepayCards = false;
			hasApplepay = false;
			// We don't want to display an error if we are getting an error here.
		} finally {
			yield spawn(logInfo, context, {
				Type: WebGivingLogInfoType.ApplePaySDK,
				SourcePageName: currentPageViewModel && WebGivingPage[currentPageViewModel.type],
				ApplePaySDKInfo: {
					CanMakePayments: hasApplepay,
					CanMakePaymentsWithActiveCard: hasApplepayCards
				}
			});
		}
	}

	setApplePayAvailable(hasApplepayCards);

	if (currentPageViewModel) {
		switch (currentPageViewModel.type) {
			case WebGivingPage.PaymentMethod:
				currentPageViewModel.setApplePayParams(getApplePayParams(context));
				break;
			case WebGivingPage.ConfirmGift:
				currentPageViewModel.setApplePayAvailability(displayApplePay(context) && hasApplepayCards);
				break;
		}

		if (loadingInitiated) {
			setAppLoading(false);
		}
	}

	GoogleAnalyticsWrapper.sendEvent('ApplePay', 'Validation', 'SDKCheck', {
		CanMakePayments: hasApplepay,
		CanMakePaymentsWithActiveCard: hasApplepayCards,
		SourcePageName: currentPageViewModel && WebGivingPage[currentPageViewModel.type]
	});
}

function updatePaymentState(context: IWebGivingSagaContext, nextPaymentState: {
	fundName: string;
	fundKey: string;
	calculatedAmountWithFees: number;
	payerElectedToCoverFees: boolean;
	payerEnabledAnniversaryReminder?: boolean;
	paymentMethodId: string | null;
	LineItems?: SplitLineItem[];
}) {
	const { appState:{ paymentState } } = context;
	paymentState.payerElectedToCoverFees = nextPaymentState.payerElectedToCoverFees;
	paymentState.payerEnabledAnniversaryReminder = nextPaymentState.payerEnabledAnniversaryReminder || paymentState.payerEnabledAnniversaryReminder;
	paymentState.calculatedAmountWithFees = nextPaymentState.calculatedAmountWithFees;
	paymentState.paymentMethodId = nextPaymentState.paymentMethodId;
	paymentState.fundName = nextPaymentState.fundName;
	paymentState.fundKey = nextPaymentState.fundKey;
	if (!!nextPaymentState.LineItems) { paymentState.LineItems = nextPaymentState.LineItems } ;
}

function getPaymentStateToConfirmPaymentRequest(context: IWebGivingSagaContext, applePayment?: ApplePayJS.ApplePayPayment): ConfirmPaymentRequest {
	const { mainViewModel, appState } = context;
	const { paymentState } = appState;
	const { viewData, hasTranslationEnabled } = mainViewModel;

	const shouldReceiveWelcomeSMS = userShouldReceiveWelcomeSMS(appState, viewData);

	const cards = appState.appContentCards;
	const emailAccData = cards && cards[cards.length - 1] || null;
	const displayedAccType = cards && cards.length ? cards[0].CardType : null;

	return paymentStateToPaymentRequest(
		viewData.PreconfiguredPaymentDetails,
		viewData.MerchantInfo,
		paymentState,
		appState.userPrefersVoiceCalls,
		shouldReceiveWelcomeSMS,
		viewData.PushpayInfo.TermsAndConditionsVersion,
		viewData.InitialQueryString,
		appState.recurringSuggestionId,
		emailAccData,
		displayedAccType,
		hasTranslationEnabled,
		applePayment
	);
}

function* handleSubmitCurrentPageSaga(context: IWebGivingSagaContext): IterableIterator<any> {
	const { mainViewModel, dataService, appState } = context;
	const { paymentState } = appState;
	const { currentPageViewModel, viewData, hasTranslationEnabled, uiConfiguration } = mainViewModel;

	currentPageViewModel.error = null;

	if (context.disableValidation !== true) {
		const currentValidationState = currentPageViewModel.validate();
		if (currentValidationState.isValid !== true) {
			currentPageViewModel.validationState = currentValidationState;
			scrollErrorIntoViewOnNextFrame();
			return;
		}
	}

	currentPageViewModel.isLoading = true;
	let extendedResponseTimeNotice = null;

	try {
		switch (currentPageViewModel.type) {
			case WebGivingPage.Give:
				yield navigatePastGivePage(context, currentPageViewModel);
				break;
			case WebGivingPage.EnterMobileNumber:
				yield callInAction(enterMobileNumberSaga, context, currentPageViewModel);
				break;
			case WebGivingPage.ConfirmMobileNumber:
				yield callInAction(confirmMobileNumberSaga, context, currentPageViewModel);
				break;
			case WebGivingPage.CreateAccount:
				const createAccountResponse: typeof webGivingActionResponse.createAccount = yield dataService.createAccount({
					request:{model: {
						FirstName: currentPageViewModel.firstName.value,
						LastName: currentPageViewModel.lastName.value,
						Email: currentPageViewModel.emailAddress.value,
						RememberMe: appState.rememberMe,
						Language: getLanguageWithFallback(hasTranslationEnabled)
					}},
				});

				appState.userWasCreated = true;
				appState.person = createAccountResponse.Person;
				yield navigateNextAfterPersonSelected(context);
				break;
			case WebGivingPage.PaymentMethod:
				switch (currentPageViewModel.paymentMethodType) {
					case PaymentMethodType.ACH:
						const { achModel } = currentPageViewModel;
						const addACHPaymentMethodResponse: typeof webGivingActionResponse.addACHPaymentMethod = yield dataService.addACHPaymentMethod({
							request:{addACHPaymentMethodRequest: {
								RoutingNumber: achModel.routingNumber.value,
							}},
						});

						if (addACHPaymentMethodResponse.Success !== true) {
							currentPageViewModel.achModel.routingNumber.setServerSideValidationError('This routing number is not valid');
							return;
						}

						mainViewModel.openPage(new WebGivingPageViewModel.ConfirmBankAccount(achModel, addACHPaymentMethodResponse.BankName));
						break;
					case PaymentMethodType.CreditCard:
						const addCreditCardPaymentMethodResponse: typeof webGivingActionResponse.addCreditCardPaymentMethod = yield dataService.addCreditCardPaymentMethod({
							request:{model: {
								CardNumber: currentPageViewModel.cardModel.cardNumber.value.replace(digitsOnly, ''),
								CardExpirationMonth: currentPageViewModel.cardModel.cardExpirationMonth,
								CardExpirationYear: currentPageViewModel.cardModel.cardExpirationYear,
								CardSecurity: currentPageViewModel.cardModel.cvv.value,
								ZipCode: currentPageViewModel.cardModel.zipCode.value,
							}},
						});

						if (addCreditCardPaymentMethodResponse.Success !== true) {
							currentPageViewModel.setError({
								errorType: WebGivingErrorType.AddPaymentMethodError,
								errorResultCode: addCreditCardPaymentMethodResponse.ResultCode,
								errorMessageLines: addCreditCardPaymentMethodResponse.ErrorMessageLines,
							});
							return;
						}

						const person = ensurePerson(appState.person);
						person.PaymentMethods = addCreditCardPaymentMethodResponse.PaymentMethods;

						paymentState.paymentMethodId = `${addCreditCardPaymentMethodResponse.LastAddedId}`;

						navigateToConfirmGiftPage(context);
						break;
					default:
						throw new Error(`Unsupported payment method: ${PaymentMethodType[currentPageViewModel.paymentMethodType]}`);
				}
				break;
			case WebGivingPage.ConfirmBankAccount:
				const person = ensurePerson(appState.person);

				if (context.disableValidation || currentPageViewModel.achModel.accountNumber.value === currentPageViewModel.accountNumberConfirm.value) {
					const { routingNumber, accountNumber, accountType } = currentPageViewModel.achModel;
					const confirmACHAccountResponse: typeof webGivingActionResponse.confirmACHAccount = yield dataService.confirmACHAccount({
						request:{
								model: {
								RoutingNumber: routingNumber.value,
								AccountNumber: accountNumber.value,
								AccountType: accountType.value,
							}
						},
					});

					if (confirmACHAccountResponse.Success !== true) {
						const vm = new WebGivingPageViewModel.PaymentMethod(
							viewData.MerchantInfo,
							person,
							null,
							Boolean(uiConfiguration.HideACH),
							PaymentMethodType.ACH,
							currentPageViewModel.achModel,
						);

						currentPageViewModel.error = {
							errorType: WebGivingErrorType.Generic,
							message: confirmACHAccountResponse.Reason,
						};
						mainViewModel.openPage(vm);
						return;
					}

					person.PaymentMethods = confirmACHAccountResponse.PaymentMethods;
					navigateToConfirmGiftPage(context);
				} else {
					currentPageViewModel.achModel.accountNumber.updateValue('');
					currentPageViewModel.achModel.updateAccountMismatchError(true);
					mainViewModel.openPage(new WebGivingPageViewModel.PaymentMethod(
						viewData.MerchantInfo,
						person,
						null,
						Boolean(uiConfiguration.HideACH),
						PaymentMethodType.ACH,
						currentPageViewModel.achModel,
						viewData.PushpayInfo.TermsAndConditionsUrl,
						viewData.PushpayInfo.PrivacyStatementUrl
					));
				}
				break;
			case WebGivingPage.ConfirmGift:
				const hasLineItems = paymentState.LineItems && paymentState.LineItems.length > 1;
				updatePaymentState(context, {
					payerElectedToCoverFees: currentPageViewModel.payerElectedToCoverFees.value,
					payerEnabledAnniversaryReminder: currentPageViewModel.payerEnabledAnniversaryReminder.value,
					calculatedAmountWithFees: currentPageViewModel.calculatedAmountWithFees,
					paymentMethodId: currentPageViewModel.paymentMethodId.value,
					fundName: currentPageViewModel.fundName,
					fundKey: currentPageViewModel.fundKey,
					LineItems: (hasLineItems
						? getLineItemsAmountWithFees(paymentState.LineItems, viewData.MerchantInfo.TransactionFeeRate, paymentState.payerElectedToCoverFees)
						: undefined)
				});

				const model = getPaymentStateToConfirmPaymentRequest(context);

				extendedResponseTimeNotice = yield spawn(extendedResponseTimeTimer, currentPageViewModel);

				try {
					const confirmPaymentResponse: typeof webGivingActionResponse.confirmPayment = yield dataService.confirmPayment({ request:{ model }, queryStringParameters:{ unique: uuid() }, timeout:extendedTimeout});
					yield callInAction(handleConfirmPaymentResponse, context, confirmPaymentResponse);

				} catch (e) {
					if (e instanceof PostError) {
						if (e.conflict) {
							const updateData: MerchantConflictResponse = e.updatedRecordForConflict;
							updateMerchantInfo(context, updateData);
							currentPageViewModel.error = merchantConfigurationChangedError;
						} else {
							if (!e.shouldReloadPage) {
								throw new ConfirmGiftPostError('ConfirmGiftPostError', viewData.MerchantInfo.PaymentLabel.NounLowerCase);
							}
							throw e;
						}
					} else {
						throw e;
					}
				} finally {
					currentPageViewModel.setResponseTimeNotice(false);
					if (extendedResponseTimeNotice) {
						yield cancel(extendedResponseTimeNotice);
					}
				}

				break;
			case WebGivingPage.ConfirmMobileNumberForPayment:
				if (currentPageViewModel.additionalInfo.isApplePayment) {
					yield callInAction(makeApplePaymentSaga, context);
					break;
				}

				const mobileNumberRequest = ensureMobileNumberRequest(appState.mobileNumberRequest);
				extendedResponseTimeNotice = yield spawn(extendedResponseTimeTimer, currentPageViewModel);

				try {
					let verifyPaymentConfirmationCodeResponse: typeof webGivingActionResponse.verifyPaymentConfirmationCode;

					const cards = appState.appContentCards;
					const emailAccData = cards && cards[cards.length - 1] || null;
					const displayedAccType = cards && cards.length ? cards[0].CardType : null;

					verifyPaymentConfirmationCodeResponse = yield dataService.verifyPaymentConfirmationCode({
						request:{model: {
							Code: currentPageViewModel.securityCode.value,
							PaymentToken: paymentState.paymentToConfirmToken as any,
							PaymentTokens: paymentState.paymentToConfirmTokens,
							RequestToken: mobileNumberRequest.mobileRequestToken,
							PayerEnabledAnniversaryReminder: paymentState.payerEnabledAnniversaryReminder,
							RememberMe: currentPageViewModel.rememberMobileNumber.value,
							ShouldSendWelcomeSms: userShouldReceiveWelcomeSMS(appState, viewData),
							RecurringSuggestionId: appState.recurringSuggestionId,
							EmailAppContentCardModel: emailAccData,
							DisplayedInWebGivingAppContentCardType: displayedAccType,
							Language: getLanguageWithFallback(hasTranslationEnabled),
							InitialUrlParameters: viewData.InitialQueryString,
							ApplePayInfo: null,
						}}, timeout: extendedTimeout
					});

					if (verifyPaymentConfirmationCodeResponse.ConfirmationCodeError === true) {
						if (verifyPaymentConfirmationCodeResponse.ConfirmationCodeInvalid) {
							currentPageViewModel.securityCode.setServerSideValidationError(verifyPaymentConfirmationCodeResponse.Reason);
						} else {
							currentPageViewModel.error = {
								errorType: WebGivingErrorType.Generic,
								message: verifyPaymentConfirmationCodeResponse.Reason,
							};
						}
						return;
					}

					if (currentPageViewModel.rememberMobileNumber.value) {
						appState.rememberMe = true;
					}

					yield callInAction(handleConfirmPaymentResponse, context, verifyPaymentConfirmationCodeResponse);
				} catch (e) {
					if (e instanceof PostError && !e.conflict && !e.shouldReloadPage) {
						throw new ConfirmGiftPostError('ConfirmGiftPostError', viewData.MerchantInfo.PaymentLabel.NounLowerCase);
					} else {
						throw e;
					}
				} finally {
					currentPageViewModel.setResponseTimeNotice(false);
					if (extendedResponseTimeNotice) {
						yield cancel(extendedResponseTimeNotice);
					}
				}

				break;
			case WebGivingPage.PaymentSuccess:
				// reset payment state
				const rememberedAmount = appState.rememberMe ? appState.paymentState.amount : undefined;
				viewData.PreconfiguredPaymentDetails = null;
				appState.paymentState = defaultPaymentState(viewData, false, rememberedAmount);
				appState.userWasCreated = false;

				configureRecurringSuggestionInformation(context, false);

				mainViewModel.openPage(new WebGivingPageViewModel.Give(appState.paymentState, viewData.MerchantInfo, mainViewModel.viewData.BrandingPackage));
				break;
			default:
				throw new Error(`WebGivingPage ${WebGivingPage[(currentPageViewModel as any).type]} is not supported`);
		}
	} finally {
		if (currentPageViewModel === mainViewModel.currentPageViewModel) {
			// only disable loading if we're still on the same page, otherwise keep isLoading to avoid unnecessary transitions
			currentPageViewModel.isLoading = false;
		}
	}
}

function* handleRecordRecurringSuggestion(context: IWebGivingSagaContext) {
	cancelRunningRecurringSuggestionRequest(context);
	yield recordRecurringSuggestionView(context);
}

function handlePreviousPageSaga(context: IWebGivingSagaContext) {
	const { mainViewModel, appState } = context;
	const { paymentState } = appState;
	const { currentPageViewModel, viewData, uiConfiguration: { SkipGivePage, SuccessBackButtonRedirectUrl } } = mainViewModel;

	function redirectBack() {
		if (SuccessBackButtonRedirectUrl) {
			mainViewModel.redirectToRedirectUrlFromSuccessPage(SuccessBackButtonRedirectUrl);
			// never ending promise since we're doing a redirect and don't want to resolve the promise early
			return async () => {
				await new Promise(() => { });
			};
		}
		return false;
	}

	switch (currentPageViewModel.type) {
		case WebGivingPage.Give:
			if (appState.previousPaymentThankYouPageDetails) {
				mainViewModel.openPage(new WebGivingPageViewModel.PaymentSuccess(appState.previousPaymentThankYouPageDetails));
			}
			break;
		case WebGivingPage.EnterMobileNumber:
		case WebGivingPage.CreateAccount:
		case WebGivingPage.ConfirmGift:
			if (currentPageViewModel.type === WebGivingPage.ConfirmGift && SkipGivePage) {
				const text = paymentState.giftType === GiftType.Recurring ? `Please confirm to schedule your`: `Please confirm your`;
				const notificationViewModel = new NotificationViewModel(`${text} ${viewData.MerchantInfo.PaymentLabel.NounLowerCase}`, 10, true, undefined, false, true);
				mainViewModel.showNotification(notificationViewModel);
			} else {
				// back to the give page
				mainViewModel.openPage(new WebGivingPageViewModel.Give(paymentState, viewData.MerchantInfo, mainViewModel.viewData.BrandingPackage));
			}
			break;
		case WebGivingPage.ConfirmMobileNumber:
			// back to EnterMobileNumber
			mainViewModel.openPage(populateEnterMobileNumberPageData(context));
			break;
		case WebGivingPage.PaymentMethod:
			// back to the give or confirm gift page
			// depending on whether this is the first payment method for the user
			if (personHasPaymentMethods(context)) {
				navigateToConfirmGiftPage(context);
			} else {
				mainViewModel.openPage(new WebGivingPageViewModel.Give(paymentState, viewData.MerchantInfo, mainViewModel.viewData.BrandingPackage));
			}
			break;
		case WebGivingPage.ConfirmMobileNumberForPayment:
			// back to the confirm gift page
			navigateToConfirmGiftPage(context);
			break;
		case WebGivingPage.ConfirmBankAccount:
			// back to the PaymentMethod page
			handleChangeBankAccountDetails(context);
			break;
		case WebGivingPage.PaymentSuccess:
			if (appState.previousPaymentThankYouPageDetails) {
				appState.previousPaymentThankYouPageDetails = null;
				configureRecurringSuggestionInformation(context, true);
				appState.paymentState = defaultPaymentState(mainViewModel.viewData, false);


				if (SkipGivePage) {
					redirectBack();
				} else {
					mainViewModel.openPage(new WebGivingPageViewModel.Give(appState.paymentState, viewData.MerchantInfo, mainViewModel.viewData.BrandingPackage));
				}
			}

			break;
		default:
			throw new Error(`WebGivingPage ${WebGivingPage[(currentPageViewModel as any).type]} is not supported`);
	}
}

function* handleConfirmPaymentResponse(context: IWebGivingSagaContext, response: ConfirmPaymentResponse, session?: ApplePaySession ): IterableIterator<any> {
	const { mainViewModel, appState } = context;
	const { paymentState } = appState;
	const { viewData } = mainViewModel;
	const { MerchantInfo, PushpayInfo: { ResetPasswordUrl } } = viewData;

	updateMerchantInfo(context, response.UpdatedMerchantDetails);

	const isSplitGivingEnabled = MerchantInfo.SplitGivingEnabled;
	const responseStatus = isSplitGivingEnabled ? getConfirmPaymentResponseStatus(response) : response.Status;

	switch (responseStatus) {
		case WebGivingPaymentResultStatus.InvalidSignedConfigWithRedirectUrl:
			if (session) {
				session.abort();
			}
			const { RedirectUrl } = response;
			if (!!RedirectUrl) {
				window.document.location.href = RedirectUrl;
			} else {
				throw new Error('WebGivingPaymentResponse returned with undefined RedirectUrl from InvalidSignedConfigWithRedirectUrl');
			}

			break;
		case WebGivingPaymentResultStatus.RequiresConfirmation:
			const { confirmedMobileNumber } = appState;
			const { ConfirmPaymentLineItemResults } = response;

			if (!confirmedMobileNumber) {
				throw new Error('Illegal state: confirmedMobileNumber must not be null');
			}

			if (session) {
				session.abort();
			}

			paymentState.paymentToConfirmToken = response.PaymentToken;
			paymentState.paymentToConfirmTokens = ConfirmPaymentLineItemResults
				? ConfirmPaymentLineItemResults.map((lineItem) => lineItem.PaymentToken)
				: null;

			appState.mobileNumberRequest = {
				mobileNumber: response.MobileNumber,
				mobileRequestToken: response.RequestToken,
			};

			mainViewModel.openPage(new WebGivingPageViewModel.ConfirmMobileNumber(
				confirmedMobileNumber.NationalNumber,
				WebGivingPage.ConfirmMobileNumberForPayment,
				appState.rememberMe,
				mainViewModel.resendCodeDisabled,
				ResetPasswordUrl,
				MerchantInfo.PaymentLabel,
				{
					currency: MerchantInfo.Currency,
					confirmationReason: response.ConfirmationReason,
					fastModeDailyLimit: response.FastModeDailyLimit,
					fastModeAvailable: response.FastModeAvailable,
				},
				{
					isApplePayment: Boolean(session)
				}
			));
			const notificationViewModel = new NotificationViewModel(confirmationCodeSentMessage(appState.userPrefersVoiceCalls, true), 10);
			mainViewModel.showNotification(notificationViewModel);
			break;
		case WebGivingPaymentResultStatus.SuspectedAccidentalDuplicate:
		case WebGivingPaymentResultStatus.Fail:
		case WebGivingPaymentResultStatus.CardFail:
		case WebGivingPaymentResultStatus.InvalidAmount:
		case WebGivingPaymentResultStatus.InvalidReferences:
		case WebGivingPaymentResultStatus.MaximumYearExceeded:
		case WebGivingPaymentResultStatus.PaymentMethodNotSupported: {
			const { currentPageViewModel, uiConfiguration } = mainViewModel;

			if (session) {
				session.abort();
			}

			if (!paymentState.paymentMethodId && !session) {
				throw new Error('Illegal state: paymentMethodId should be set before confirming payment for Card and Ach payments');
			}

			const person = ensurePerson(appState.person);
			switch (currentPageViewModel.type) {
				case WebGivingPage.PaymentMethod:
				case WebGivingPage.ConfirmGift:
					currentPageViewModel.error = {
						errorType: WebGivingErrorType.PaymentError,
						errorReason: responseStatus,
						message: response.ResultDescription,
						failedPaymentMethodId: paymentState.paymentMethodId || undefined,
						paymentLabel: MerchantInfo.PaymentLabel,
						singlePaymentMethod: (person.PaymentMethods || []).length === 1,
					};
					break;
				case WebGivingPage.ConfirmMobileNumberForPayment:
					const agreedTermsAndConditionsOutOfDate = person.TermsAndConditionsVersion !== viewData.PushpayInfo.TermsAndConditionsVersion;
					const nextViewModel = new WebGivingPageViewModel.ConfirmGift(
						paymentState,
						person.PaymentMethods,
						MerchantInfo,
						agreedTermsAndConditionsOutOfDate,
						uiConfiguration.PaymentScreenText,
						Boolean(uiConfiguration.HideACH),
						Boolean(uiConfiguration.SkipGivePage),
						displayApplePay(context),
						viewData.PushpayInfo.TermsAndConditionsUrl,
						viewData.PushpayInfo.PrivacyStatementUrl,
					);
					nextViewModel.error = {
						errorType: WebGivingErrorType.PaymentError,
						errorReason: responseStatus,
						message: response.ResultDescription,
						failedPaymentMethodId: paymentState.paymentMethodId || undefined,
						paymentLabel: MerchantInfo.PaymentLabel,
						singlePaymentMethod: person.PaymentMethods.length === 1,
					};
					mainViewModel.openPage(nextViewModel);
					break;
			}
			break;
		}
		case WebGivingPaymentResultStatus.Poll:
			yield callInAction(pollPaymentResult, context, response);
			break;
		case WebGivingPaymentResultStatus.TimedOut:
		case WebGivingPaymentResultStatus.Success:
		case WebGivingPaymentResultStatus.Processing:
		case WebGivingPaymentResultStatus.Delayed:
			appState.recurringSuggestionId = null;

			const person = ensurePerson(appState.person);
			const { PaymentLabel, Currency: currency, MobileAppName, GetAppUrl, TradingName, HasCustomApp, BrandingEnabled } = MerchantInfo;
			const welcomeSmsSent = response.WelcomeSMSHasBeenSent;
			const appContentCardData = appState.appContentCards && appState.appContentCards.length ? appState.appContentCards[0] : null;
			const { uiConfiguration } = mainViewModel;

			if (session) {
				session.completePayment({ status: ApplePaySession.STATUS_SUCCESS });
			}

			const { ConfirmPaymentLineItemResults: lineItemResults, UpdatedMerchantDetails } = response;
			const hasSplitLineItemResults = lineItemResults && lineItemResults.length > 1;
			const customFields = UpdatedMerchantDetails && UpdatedMerchantDetails.MerchantInfo.CustomFields;
			const splitLineItemResults = isSplitGivingEnabled && hasSplitLineItemResults
				? constructSplitLineItemResults(lineItemResults, customFields)
				: [];

			const data: IPagePaymentSuccessData = {
				isRecurring: isRecurringPayment(paymentState.giftType),
				giftAmount: paymentState.calculatedAmountWithFees,
				fundName: paymentState.fundName,
				emailAddress: person.EmailAddress,
				paymentLabel: PaymentLabel,
				receiptSent: response.ReceiptHasBeenSent,
				merchantCurrency: Currency[currency],
				successThankYouMessage: response.SuccessThankYouMessage ? atob(response.SuccessThankYouMessage) : '',
				merchantAppName: MobileAppName,
				getAppUrl: GetAppUrl && response.AppConnectData ? addQueryStringParameter(GetAppUrl, 'data', response.AppConnectData) : GetAppUrl,
				merchantName: TradingName,
				stillProcessing: responseStatus !== WebGivingPaymentResultStatus.Success,
				wasDelayed: responseStatus === WebGivingPaymentResultStatus.Delayed,
				recurringStartDate: paymentState.giftType === GiftType.Recurring ? paymentState.recurringInfo.startDate : undefined,
				recurringFrequency: paymentState.giftType === GiftType.Recurring ? paymentState.recurringInfo.frequency : undefined,
				hasWelcomeSmsBeenSent: welcomeSmsSent,
				hasCustomApp: HasCustomApp,
				hideAppCallToAction: viewData.HideAppCallToAction,
				isBranded: BrandingEnabled,
				recurringInfo: null,
				fastModeAvailable: response.FastModeAvailable,
				fastModeCurrentlyEnabled: response.FastModeCurrentlyEnabled,
				fastModeDailyLimit: response.FastModeDailyLimit,
				appContentCard: appContentCardData,
				isOneTimeFutureGift: paymentState.giftType === GiftType.Recurring ? false : paymentState.isOneTimeFutureGift,
				oneTimeGiftDate: paymentState.giftType === GiftType.Recurring ? null : paymentState.giftDate,
				thankYouText: uiConfiguration.ThankYouText || '',
				thankYouTextHtml: uiConfiguration.SkipGivePage ?  uiConfiguration.ThankYouText : '',
				returnButtonText:  uiConfiguration.ReturnButtonText || '',
				returnButtonUrl:  uiConfiguration.ReturnButtonUrl || '',
				thankYouAnimation: response.SuccessThankYouAnimationType,
				splitLineItemResults: splitLineItemResults
			};

			if (appState.rememberMe) {
				setUserRememberedAmount(MerchantInfo.MerchantId, person.SignedPersonId, appState.paymentState.amount);
			}

			// This gets set on the server side after a payment
			if (!!person) {
				person.TermsAndConditionsVersion = viewData.PushpayInfo.TermsAndConditionsVersion;
			}
			if (!appState.fromRecurringSuggestion) {
				data.recurringInfo = data.isRecurring ? null : enableRecurringSuggestion(appState.recurringSuggestion);
				yield cancelRunningRecurringSuggestionRequest(context);
			} else {
				appState.fromRecurringSuggestion = false;
			}

			let redirectUrl = response.RedirectUrl || getRedirectUrl(viewData.PreconfiguredPaymentDetails, response.PaymentToken);

			handleSetRecurringSuggestionSuccess(context, postStatsData);
			yield spawn(handleRecordRecurringSuggestion, context);

			appState.previousPaymentThankYouPageDetails = data;

			if (redirectUrl) {
				mainViewModel.redirectToRedirectUrlFromSuccessPage(redirectUrl);
				// never ending promise since we're doing a redirect and don't want to resolve the promise early
				yield new Promise(() => { });
			} else {
				mainViewModel.openPage(new WebGivingPageViewModel.PaymentSuccess(data));

				if (uiConfiguration.SkipGivePage) {
					clearPreconfiguredUrl();
				}

				if (!person.HasVerifiedEmailAddress) {
					const emailAddress = person.EmailAddress;
					const donorPortalUrl = viewData.PushpayInfo.DonorPortalUpdateEmailUrl;

					const EmailNotification = createEmailVerificationNotification({
						items:[{
							text: `Keep your account secure by verifying {{emailAddress}}, from the receipt we sent you. No longer using this email?`,
							textParams:{emailAddress},
							action:{
								label: 'Update email address',
								url: `${donorPortalUrl}`
							}
						}]
					});
					mainViewModel.showNotification(EmailNotification);
				}

				yield spawn(logInfo, context, { Type: WebGivingLogInfoType.MissingTranslations });
			}
			break;
		default:
			if (session) {
				session.abort();
			}
			const unsupportedResponseStatus: never = responseStatus;
			throw new Error(`WebGivingPaymentResultStatus[${unsupportedResponseStatus}] is not supported`);
	}

	updatePersonPaymentMethods(context, response.UpdatedPersonPaymentMethods);
}


function* pollPaymentResult(context: IWebGivingSagaContext, response: ConfirmPaymentResponse): IterableIterator<any> {
	const { dataService } = context;
	let retryCount = 0;

	while (true) {
		yield delay(1000);

		let pollResponse: typeof webGivingActionResponse.pollResult;

		try {
			pollResponse = yield dataService.pollResult({
				request:{model: {
					PaymentToken: response.PaymentToken,
				}},
			});
		} catch (ex) {
			retryCount++;
			if (retryCount > 5) {
				response.Status = WebGivingPaymentResultStatus.TimedOut;
				yield callInAction(handleConfirmPaymentResponse, context, response);
				return;
			} else {
				continue;
			}
		}

		if (pollResponse.Status !== WebGivingPaymentResultStatus.Poll) {
			yield callInAction(handleConfirmPaymentResponse, context, pollResponse);
			break;
		}
	}
}

function getApplePayPaymentRequest(context: IWebGivingSagaContext): ApplePayJS.ApplePayPaymentRequest | null {
	const {
		mainViewModel: {
			viewData: { MerchantInfo, DonorPortalBaseUrl },
		},
		appState: { paymentState },
	} = context;

	const merchantConfig = getApplePayNetworksAndCapabilities(MerchantInfo);
	if (merchantConfig) {
		// Feature flag for Apple Pay Recurring
		if (MerchantInfo.SupportsApplePayRecurring === false) {
			const requiredBillingContactFields: ApplePayJS.ApplePayContactField[] = [ "postalAddress" ];
			const amount = paymentState.calculatedAmountWithFees.toString();
			const shippingMethods: ApplePayJS.ApplePayShippingMethod[] = [];
			const label = MerchantInfo.PaymentLabel.NounSentenceCase;
			const hasLineItems = paymentState.LineItems && paymentState.LineItems.length > 1;
			const lineItems = hasLineItems
				? paymentState.LineItems.map(({ FundKey, Amount, AmountWithFees }) => ({
					label: getFundName(FundKey, MerchantInfo.CustomFields),
					amount: AmountWithFees ? AmountWithFees.toFixed(2) : Amount.toFixed(2)
				}))
				: [{ label, amount }];

			return {
				currencyCode: Currency[MerchantInfo.Currency],
				countryCode: Country[MerchantInfo.Country],
				lineItems,
				total: {
					label: `${MerchantInfo.ApplePayMerchantName}`,
					amount,
				},
				supportedNetworks: merchantConfig.supportedNetworks,
				merchantCapabilities: merchantConfig.merchantCapabilities,
				shippingMethods,
				requiredBillingContactFields,
			};
		}

		if (paymentState.giftType === GiftType.OneTime) {
			return getApplePayOneOffRequest(paymentState, MerchantInfo, DonorPortalBaseUrl, { supportedNetworks: merchantConfig.supportedNetworks, merchantCapabilities: merchantConfig.merchantCapabilities });
		} else if (paymentState.giftType === GiftType.Recurring) {
			return getApplePayRecurringPaymentRequest(paymentState, MerchantInfo, DonorPortalBaseUrl, { supportedNetworks: merchantConfig.supportedNetworks, merchantCapabilities: merchantConfig.merchantCapabilities });
		}
		else {
			throw new Error('Illegal state: unknown Gift Type');
		}
	}

	return null;
}

function getApplePayRecurringPaymentRequest(
	paymentState: IWebGivingCommonPaymentState & IWebGivingRecurringPaymentState,
	merchantInfo: WebGivingMerchant,
	donorPortalBaseUrl: string,
	merchantConfig: { supportedNetworks: ApplePaySupportedNetworks[]; merchantCapabilities: ApplePayMerchantCapabilities[]; }) {

	const requiredBillingContactFields: ApplePayJS.ApplePayContactField[] = ["postalAddress"];
	const amount = paymentState.calculatedAmountWithFees.toString();
	const shippingMethods: ApplePayJS.ApplePayShippingMethod[] = [];
	const label = merchantInfo.PaymentLabel.NounSentenceCase;

	const intervalParams = mapFrequencyCodeToApplePayInterval(paymentState.recurringInfo.frequency);

	const regularBilling: ApplePayJS.ApplePayLineItem = {
		label: `Recurring ${label}`, // Hardcoded values for internal verification purposes
		amount: amount,
		paymentTiming: 'recurring',
		recurringPaymentStartDate: paymentState.recurringInfo.startDate,
		recurringPaymentIntervalUnit: intervalParams.intervalUnit,
		recurringPaymentIntervalCount: intervalParams.intervalCount,
	};

	const recurringPayment: ApplePayJS.ApplePayRecurringPaymentRequest = {
		paymentDescription: `Recurring ${label}`, // Hardcoded values for internal verification purposes
		regularBilling: regularBilling,
		managementURL: donorPortalBaseUrl,
		tokenNotificationURL: "https://pushpay.com/", // Hardcoded values for internal verification purposes
	};

	const req = {
		currencyCode: Currency[merchantInfo.Currency],
		countryCode: Country[merchantInfo.Country],
		lineItems: [regularBilling],
		total: {
			label: `${merchantInfo.ApplePayMerchantName}`,
			amount,
		},
		supportedNetworks: merchantConfig.supportedNetworks,
		merchantCapabilities: merchantConfig.merchantCapabilities,
		shippingMethods,
		requiredBillingContactFields,
		recurringPaymentRequest: recurringPayment
	};

	if(paymentState.recurringInfo.frequency == FrequencyCode.FirstAndFifteenth) {
		req.recurringPaymentRequest.billingAgreement = 'Set up a recurring gift on the 1st and 15th monthly.';
	}

	return req;
}

function getApplePayOneOffRequest(
	paymentState: IWebGivingCommonPaymentState & IWebGivingOneTimePaymentState,
	merchantInfo: WebGivingMerchant,
	donorPortalBaseUrl: string,
	merchantConfig: { supportedNetworks: ApplePaySupportedNetworks[]; merchantCapabilities: ApplePayMerchantCapabilities[]; }
) {
	const requiredBillingContactFields: ApplePayJS.ApplePayContactField[] = ["postalAddress"];
	const amount = paymentState.calculatedAmountWithFees.toString();
	const shippingMethods: ApplePayJS.ApplePayShippingMethod[] = [];
	const label = merchantInfo.PaymentLabel.NounSentenceCase;
	const hasLineItems = paymentState.LineItems && paymentState.LineItems.length > 1;
	const lineItems = hasLineItems
		? paymentState.LineItems.map(({ FundKey, Amount, AmountWithFees }) => ({
			label: getFundName(FundKey, merchantInfo.CustomFields),
			amount: AmountWithFees ? AmountWithFees.toFixed(2) : Amount.toFixed(2)
		}))
		: [{ label, amount }];

	const baseRequest = {
		currencyCode: Currency[merchantInfo.Currency],
		countryCode: Country[merchantInfo.Country],
		total: {
			label: merchantInfo.ApplePayMerchantName,
			amount,
		},
		supportedNetworks: merchantConfig.supportedNetworks,
		merchantCapabilities: merchantConfig.merchantCapabilities,
		shippingMethods,
		requiredBillingContactFields,
	};

	if (merchantInfo.SupportsApplePayRecurring && paymentState.isOneTimeFutureGift) {
		// TODO: translate this
		const futureGiftLabel = `Future Gift`;
		const deferredBilling: ApplePayJS.ApplePayLineItem = {
			label: futureGiftLabel,
			amount: amount,
			paymentTiming: 'deferred',
			deferredPaymentDate: paymentState.giftDate
		};

		return {
			...baseRequest,
			lineItems: [deferredBilling],
			deferredPaymentRequest: {
				paymentDescription: merchantInfo.ApplePayMerchantName,
				deferredBilling,
				managementURL: donorPortalBaseUrl,
			},
		};
	}

	return {
		...baseRequest,
		lineItems,
	};
}

function getApplePayParams(context: IWebGivingSagaContext): PaymentMethodApplePayParams | null {
	const {
		mainViewModel: { applePayAvailable, viewData, uiConfiguration },
		appState: { paymentState, person  },
	} = context;

	if (displayApplePay(context)) {
		return {
			amount: paymentState.amount,
			applePayAvailable: applePayAvailable,
			electedToCoverFees: paymentState.payerElectedToCoverFees,
			oneTimeGiftDateLabel: generateOneTimeGiftDateLabel(paymentState),
			showTermsAndConditions: person ? person.TermsAndConditionsVersion !== viewData.PushpayInfo.TermsAndConditionsVersion : false,
			fundName: getSelectedFundName(paymentState.referenceFieldValues, viewData.MerchantInfo.CustomFields),
			paymentDetailsText: uiConfiguration.SkipGivePage ? uiConfiguration.PaymentScreenText : '',
			lineItems: paymentState.LineItems,
		};
	}

	return null;
}

function ensurePerson(person: WebGivingPersonModel | null) {
	if (!person) {
		throw new Error('Illegal state: person must not be null');
	}

	return person;
}

function ensureMobileNumberRequest(mobileNumberRequest: IWebGivingMobileNumberRequest | null) {
	if (!mobileNumberRequest) {
		throw new Error('Illegal state: mobile number request must be defined');
	}

	return mobileNumberRequest;
}

function* enterMobileNumberSaga(context: IWebGivingSagaContext, currentPageviewModel: WebGivingPageViewModel.EnterMobileNumber): IterableIterator<any> {
	const { mainViewModel, dataService, appState } = context;
	const { ResetPasswordUrl } = context.mainViewModel.viewData.PushpayInfo;
	const { hasTranslationEnabled } = context.mainViewModel;

	const response: typeof webGivingActionResponse.enterMobileNumber = yield dataService.enterMobileNumber({
		request: {
			model: {
				Country: Country[currentPageviewModel.mobileCountry.value],
				MobileNumber: currentPageviewModel.mobileNumber.value,
				UseVoiceCall: appState.userPrefersVoiceCalls,
				Language: getLanguageWithFallback(hasTranslationEnabled),
			}},
	});

	if (response.Success !== true) {
		currentPageviewModel.mobileNumber.setServerSideValidationError(response.Reason);
		return;
	}

	appState.mobileNumberRequest = {
		mobileNumber: response.MobileNumber,
		mobileRequestToken: response.RequestToken,
	};

	mainViewModel.openPage(new WebGivingPageViewModel.ConfirmMobileNumber(
		response.MobileNumber.NationalNumber,
		WebGivingPage.ConfirmMobileNumber,
		appState.rememberMe,
		mainViewModel.resendCodeDisabled,
		ResetPasswordUrl,
		mainViewModel.viewData.MerchantInfo.PaymentLabel,
	));

	const notificationViewModel = new NotificationViewModel(confirmationCodeSentMessage(appState.userPrefersVoiceCalls), 10);
	mainViewModel.showNotification(notificationViewModel);
}

function handleConfirmationCodeError(currentPageViewModel: WebGivingPageViewModel.ConfirmMobileNumber, confirmationCodeResponse: ConfirmationCodeErrorResponse)  {
	if (confirmationCodeResponse.ConfirmationCodeInvalid) {
		currentPageViewModel.securityCode.setServerSideValidationError(confirmationCodeResponse.Reason);
	} else {
		currentPageViewModel.error = {
			errorType: WebGivingErrorType.Generic,
			message: confirmationCodeResponse.Reason,
		};
	}
}

function* confirmMobileNumberSaga(context: IWebGivingSagaContext, currentPageViewModel: WebGivingPageViewModel.ConfirmMobileNumber): IterableIterator<any> {

	const { mainViewModel, dataService, appState } = context;
	const { mobileNumberRequest } = appState;

	if (!mobileNumberRequest) {
		throw new Error('Illegal state: mobile number request must be defined');
	}

	const confirmationCodeResponse: typeof webGivingActionResponse.enterMobileNumberConfirmationCode =
		yield dataService.enterMobileNumberConfirmationCode({
			request:{model: {
				RememberMe: currentPageViewModel.rememberMobileNumber.value,
				Code: currentPageViewModel.securityCode.value,
				RequestToken: mobileNumberRequest.mobileRequestToken,
				NormalizedInternationalNumber: mobileNumberRequest.mobileNumber.NormalizedInternationalNumber,
			}},
		});

	if (confirmationCodeResponse.ConfirmationCodeError === true) {
		yield call(handleConfirmationCodeError, currentPageViewModel, confirmationCodeResponse);
		return;
	}

	if (currentPageViewModel.rememberMobileNumber.value) {
		appState.rememberMe = true;
	}
	appState.confirmedMobileNumber = confirmationCodeResponse.ConfirmedMobileNumber;

	if (!confirmationCodeResponse.Person) {
		mainViewModel.openPage(populateCreateAccountPageData(mainViewModel.viewData));
	} else {
		appState.person = confirmationCodeResponse.Person;
		yield navigateNextAfterPersonSelected(context);
	}
}

function cancelRunningRecurringSuggestionRequest(context: IWebGivingSagaContext): void {
	const { appState: { person } } = context;
	try {
		if (person && recurringSuggestionRequest && recurringSuggestionRequest.isRunning()) {
			recurringSuggestionRequest.cancel();
		}
	} catch {
		// We don't want to display an error if we are getting an exception on logging.
	}
}

function* recordRecurringSuggestionView(context: IWebGivingSagaContext): IterableIterator<any> {
	const { appState: { paymentState, person, recurringSuggestion } } = context;
	try {
		if (person) {
			yield spawn(callRecordRecurringSuggestionView, {
				context,
				model: {
					FundKey: paymentState.fundKey,
					SignedPayerId: person.SignedPersonId,
					...(recurringSuggestion && {
						Frequency: recurringSuggestion.Frequency,
						RecurrenceDate: recurringSuggestion.StartOnDate || new Date(),
						Amount: recurringSuggestion.Amount || 0,
					}),
				},
			});
		}
	} catch {
		// We don't want to display an error if we are getting an exception on logging.
	}
}

function* callRecordRecurringSuggestionView(data: { context: IWebGivingSagaContext, model: RecurringSuggestionModel }) {
	const { model, context: { dataService, appState } } = data;
	appState.recurringSuggestionId = yield dataService.recordRecurringSuggestionView({  request:{model} });
	if (!model.Frequency) {
		appState.recurringSuggestionId = null;
	}
}

function* getRecurringSuggestion(context: IWebGivingSagaContext): IterableIterator<any> {
	const { mainViewModel, dataService, appState } = context;
	const { paymentState, person } = appState;
	const { PreconfiguredPaymentDetails, MerchantInfo } = mainViewModel.viewData;
	const fromPreconfiguredUrl = mainViewModel.viewData.PreconfiguredPaymentDetails && mainViewModel.viewData.PreconfiguredPaymentDetails.RecurringSuggestionEnabled;

	const fundKey = getSelectedFundKey(paymentState.referenceFieldValues, mainViewModel.viewData.MerchantInfo.CustomFields);

	if (fromPreconfiguredUrl && PreconfiguredPaymentDetails) {
		appState.recurringSuggestion = {
			Frequency: PreconfiguredPaymentDetails.RecurringFrequency || FrequencyCode.Monthly,
			FundKey: '',
			Amount: PreconfiguredPaymentDetails.Amount || 0,
			Currency: MerchantInfo.Currency,
			StartOnDate: PreconfiguredPaymentDetails.RecurringStartDate || new Date(),
			ShowSuggestion: true,
		};
	}

	try {
		if (person && paymentState.giftType === GiftType.OneTime && !appState.previousPaymentThankYouPageDetails && !fromPreconfiguredUrl) {
			appState.recurringSuggestion = null;

			const response: typeof webGivingActionResponse.getRecurringSuggestion = yield dataService.getRecurringSuggestion({
				request:{query: {
					FundKey: fundKey,
					SignedPayerId: person.SignedPersonId,
					Amount: paymentState.amount,
				}},
			});
			if (response.ShowSuggestion) {
				if (context.mainViewModel.currentPage === WebGivingPage.PaymentSuccess) {
					const additionalData: { [key: string]: string } = {
						'Event': 'RecurringSuggestedError',
						'Message': 'User got suggestion too late',
						'Amount': response.Amount.toString(),
						'Frequency': (response.Frequency || '').toString(),
						'FundId': response.FundKey,
						'ShowSuggestion': response.ShowSuggestion.toString(),
						'StartOnDate': (response.StartOnDate || '').toString(),
					};
					response.Frequency = null;
					postStatsData(context, additionalData);
				} else {
					appState.recurringSuggestion = response;
				}
			}
		}
	} catch {
		//We don't want to change the behavior of the payment journey. In case of error from the server the backend will raygun it.
	}
}

function* navigateNextAfterPersonSelected(context: IWebGivingSagaContext): IterableIterator<any> {
	const { person } = context.appState;
	if (!person) {
		throw new Error('navigateNextAfterPersonSelected must not be called before mainViewModel.person is set');
	}

	setNewCRSFoken(person.NewCSRFToken);
	recurringSuggestionRequest = yield spawn(getRecurringSuggestion, context);

	navigateToConfirmGiftPage(context);
}

function personHasPaymentMethods({ appState }: IWebGivingSagaContext): boolean {
	const { person } = appState;

	if (person === null) {
		throw new Error('personHasPaymentMethods must not be called before mainViewModel.person is set');
	}

	return person.PaymentMethods && person.PaymentMethods.length > 0;
}

export function* navigatePastGivePage(context: IWebGivingSagaContext, givePageViewModel: WebGivingPageViewModel.Give): IterableIterator<any> {
	const { mainViewModel, appState } = context;
	const { paymentState } = appState;
	const {viewData, brandingPackage } = mainViewModel;

	const {
		amount: { value: amountValue },
		giftType: { value: giftTypeValue },
		frequency: { value: frequencyValue },
		startDate: { value: startDateValue },
		endType: { value: endTypeValue },
		endDate: { value: endDateValue },
		endOccurrences: { value: endOccurrencesValue },
		oneTimeGiftDate: { value: oneTimeGiftDateValue },
		merchantInfo: { CustomFields },
		referenceFieldsViewModel,
		lineItemsViewModel,
	} = givePageViewModel;

	givePageViewModel.brandingPackage = brandingPackage;
	paymentState.amount = amountValue;
	paymentState.giftType = giftTypeValue;

	if (givePageViewModel.isSplitGivingAllowed) {
		paymentState.LineItems = lineItemsViewModel.splitLineItems;
	}

	if (paymentState.giftType === GiftType.OneTime) {
		paymentState.giftDate = oneTimeGiftDateValue instanceof Date
			? oneTimeGiftDateValue
			: new Date();

		paymentState.isOneTimeFutureGift = isAfterToday(paymentState.giftDate);
	}

	if (paymentState.giftType === GiftType.Recurring) {
		if (!(startDateValue instanceof Date)) {
			throw new Error('Invalid startDateValue');
		}

		paymentState.recurringInfo = {
			frequency: frequencyValue,
			startDate: startDateValue,
			endType: endTypeValue,
			endDate: endDateValue,
			endOccurrences: endOccurrencesValue,
		};
	}

	paymentState.referenceFieldValues = CustomFields.map(({ Key, Type }) => {
		return {
			Key,
			Type,
			Value: referenceFieldsViewModel[Key].value,
		};
	});

	if (appState.person) {
		yield navigateNextAfterPersonSelected(context);
	} else if (appState.confirmedMobileNumber) {
		mainViewModel.openPage(populateCreateAccountPageData(viewData));
	} else {
		mainViewModel.openPage(populateEnterMobileNumberPageData(context));
	}
}

export function navigateToConfirmGiftPage(context: IWebGivingSagaContext, setCSRFToken?: boolean) {
	const { appState, mainViewModel } = context;
	const { person, paymentState } = appState;
	const { viewData, uiConfiguration } = mainViewModel;

	if (person === null) {
		throw new Error('navigateToConfirmGiftPage must not be called before mainViewModel.person is set');
	}

	if (uiConfiguration.SkipGivePage && setCSRFToken) {
		setNewCRSFoken(person.NewCSRFToken);
	}

	if (personHasPaymentMethods(context)) {
		const agreedTermsAndConditionsOutOfDate = person.TermsAndConditionsVersion !== viewData.PushpayInfo.TermsAndConditionsVersion;
		var confirmGiftModel = new WebGivingPageViewModel.ConfirmGift(
			paymentState,
			person.PaymentMethods,
			mainViewModel.viewData.MerchantInfo,
			agreedTermsAndConditionsOutOfDate,
			uiConfiguration.SkipGivePage ? uiConfiguration.PaymentScreenText : '',
			Boolean(uiConfiguration.HideACH),
			Boolean(uiConfiguration.SkipGivePage),
			displayApplePay(context),
			viewData.PushpayInfo.TermsAndConditionsUrl,
			viewData.PushpayInfo.PrivacyStatementUrl,
			);

		mainViewModel.openPage(confirmGiftModel);
	} else {
		mainViewModel.openPage(
			new WebGivingPageViewModel.PaymentMethod(
				viewData.MerchantInfo,
				person,
				getApplePayParams(context),
				Boolean(uiConfiguration.HideACH),
				undefined,
				undefined,
				viewData.PushpayInfo.TermsAndConditionsUrl,
				viewData.PushpayInfo.PrivacyStatementUrl
			)
		);
	}
}

function* requestConfirmationCode(context: IWebGivingSagaContext, userPrefersVoiceCalls: boolean | undefined) {
	const { appState } = context;

	if (typeof userPrefersVoiceCalls === 'boolean') {
		appState.userPrefersVoiceCalls = userPrefersVoiceCalls;
	}

	yield callInAction(handleSubmitCurrentPageSaga, context);
}

function handleChangePayerCoversFees(context: IWebGivingSagaContext, payerCoversFees: boolean) {
	const { appState: { paymentState } } = context;

	paymentState.payerElectedToCoverFees = payerCoversFees;
}

function handleRecordPageStats(context: IWebGivingSagaContext, returnUrl?: string) {
	let additionalData: { [key: string]: string } = {};

	additionalData['MerchantRecurringByDefault'] = context.mainViewModel.viewData.MerchantInfo.RecurringByDefault.toString();

	if (returnUrl) {
		additionalData['ReturnUrl'] = returnUrl;
	}

	postStatsData(context, additionalData);
}

function handleGiveAgain(context: IWebGivingSagaContext) {
	const additionalData = { UserAction: 'GiveAgain' };
	postStatsData(context, additionalData);
}

function postStatsData(context: IWebGivingSagaContext, additionalData: { [key: string]: string } | null) {
	var service = getWebGivingDataService();

	let stats: PageStatsRequest = {
		PageName: WebGivingPage[context.mainViewModel.currentPage],
		PaymentIdentifier: context.appState.paymentState.identifier,
		BrandingElements: null,
		Timestamp: convertLocalDateToUtcForLogs(moment()),
		AdditionalData: additionalData,
	};

	if (context.mainViewModel.brandingPackage) {
		const brandingElements: string[] = [];
		var properties = Object.keys(context.mainViewModel.brandingPackage);
		for (let index in properties) {

			var brandedElement = !!(<any>context.mainViewModel.brandingPackage)[properties[index]];
			if (brandedElement) {
				brandingElements.push(properties[index]);
			}
		}
		stats.BrandingElements = brandingElements;
	}

	service.recordPageStats({request:{ model: stats }}, () => context.appState)
		.catch(e => reportError(e, context.mainViewModel.currentPage));
}

function* logInfo(context: IWebGivingSagaContext, log: Required<Pick<WebGivingLogInfoRequest, 'Type'>> & Partial<WebGivingLogInfoRequest>) {
	const { dataService, mainViewModel } = context;
	const { hasTranslationEnabled } = mainViewModel;

	if (log.Type === WebGivingLogInfoType.MissingTranslations){
		yield delay(500); // Delayed to allow for missing keys from i18n
		if (hasTranslationEnabled &&
			translationLogging.hasESKeys()
		) {
			log.Translations = JSON.stringify(translationLogging.getESKeys());
			translationLogging.clearESKeys();
		}
	}

	try {
		// casting here as typescript is pedantic about null != undefined;
		yield dataService.logInfo({ request: { request: log as WebGivingLogInfoRequest } });
	} catch (error) {
	// Fire and forget we don't care about errors here
	}
}

function* requestResendOfConfirmationCode(context: IWebGivingSagaContext, userPrefersVoiceCalls: boolean | undefined) {
	const { mainViewModel, appState, dataService } = context;
	const { paymentState: { paymentToConfirmToken, paymentToConfirmTokens } } = appState;
	const currentPage = mainViewModel.currentPageViewModel;
	const { hasTranslationEnabled } = mainViewModel;

	if (typeof userPrefersVoiceCalls === 'boolean') {
		appState.userPrefersVoiceCalls = userPrefersVoiceCalls;
	}

	if (!(currentPage instanceof WebGivingPageViewModel.ConfirmMobileNumber)) {
		throw new Error(`Expected '${WebGivingPage[WebGivingPage.ConfirmMobileNumber]}' page but got '${WebGivingPage[currentPage.type]}'`);
	}

	const mobileNumberRequest = ensureMobileNumberRequest(appState.mobileNumberRequest);

	mainViewModel.resendCodeDisabled.set(true);

	try {
		switch (currentPage.type) {
			case WebGivingPage.ConfirmMobileNumber:
				const resendResponse: typeof webGivingActionResponse.resendConfirmationCode = yield dataService.resendConfirmationCode({
					request: {
						model: {
							NormalizedInternationalNumber: mobileNumberRequest.mobileNumber.NormalizedInternationalNumber,
							UseVoiceCall: appState.userPrefersVoiceCalls,
							Language: getLanguageWithFallback(hasTranslationEnabled),
						}},
				});

				if (resendResponse.Success !== true) {
					currentPage.error = {
						errorType: WebGivingErrorType.Generic,
						message: resendResponse.Reason,
					};
					return;
				}

				appState.mobileNumberRequest = {
					mobileNumber: resendResponse.MobileNumber,
					mobileRequestToken: resendResponse.RequestToken,
				};
				break;
			case WebGivingPage.ConfirmMobileNumberForPayment:
				const resendForPaymentresponse: typeof webGivingActionResponse.resendConfirmPaymentCode = yield dataService.resendConfirmPaymentCode({
					request:{
						model: {
							PaymentToken: paymentToConfirmToken as string,
							UseVoiceCall: appState.userPrefersVoiceCalls,
							Language: getLanguageWithFallback(hasTranslationEnabled),
							PaymentTokens: paymentToConfirmTokens,
						}},
				});

				appState.mobileNumberRequest = {
					mobileNumber: resendForPaymentresponse.MobileNumber,
					mobileRequestToken: resendForPaymentresponse.RequestToken,
				};
				break;
			default:
				throw new Error('Illegal state: request type must be ConfirmMobileNumber or ConfirmMobileNumberForPayment');
		}

		const notificationText = confirmationCodeSentMessage(appState.userPrefersVoiceCalls);

		const notificationViewModel = new NotificationViewModel(notificationText, 10);
		mainViewModel.showNotification(notificationViewModel);

		yield delay(20000);
	}
	finally {
		mainViewModel.resendCodeDisabled.set(false);
	}
}

function populateCreateAccountPageData(webGivingViewModel: WebGivingViewModel): PageCreateAccountViewModel {
	const { UIConfiguration, PreconfiguredPaymentDetails } = webGivingViewModel;
	const preconfiguredPaymentDetails = PreconfiguredPaymentDetails;

	if (preconfiguredPaymentDetails) {
		return new PageCreateAccountViewModel(
			preconfiguredPaymentDetails.FirstName || '',
			preconfiguredPaymentDetails.LastName || '',
			preconfiguredPaymentDetails.EmailAddress || '',
			(UIConfiguration.AccountCreationText) || ''
		);
	}
	return new PageCreateAccountViewModel();
}

function populateEnterMobileNumberPageData({ mainViewModel, appState }: IWebGivingSagaContext): PageEnterMobileNumberViewModel {
	const { viewData: { MerchantInfo, PreconfiguredPaymentDetails } } = mainViewModel;
	let mobileNumber = '';
	let mobileCountry = MerchantInfo.Country;

	if (appState.mobileNumberRequest) {
		mobileNumber = appState.mobileNumberRequest.mobileNumber.NormalizedInternationalNumber;
	} else if (PreconfiguredPaymentDetails) {
		mobileNumber = PreconfiguredPaymentDetails.MobileNumber || '';
	}

	return new PageEnterMobileNumberViewModel(
		mobileNumber,
		mobileCountry,
	);
}

function handleResetPageBecauseOfError(reason: ResetPageReason | undefined, context: IWebGivingSagaContext) {
	const { appState, mainViewModel } = context;
	if (reason && [ResetPageReason.PersonNotFound, ResetPageReason.DuplicateMobileNumber].indexOf(reason) > -1) {
		// Something has gone badly wrong when identifying the current user. Clear identifying details so we can start afresh.
		clearIdentifyingDetails(appState);
		window.location.reload(true);
	} else {
		mainViewModel.openPage(new WebGivingPageViewModel.Give(appState.paymentState, mainViewModel.viewData.MerchantInfo, mainViewModel.viewData.BrandingPackage));
	}
}

function clearIdentifyingDetails(appState: IWebGivingAppState) {
	appState.person = null;
	appState.confirmedMobileNumber = null;
	clearUserRemembered();
}

function* handleSignOut(context: IWebGivingSagaContext) {
	const { mainViewModel } = context;
	const { viewData } = mainViewModel;
	let result: typeof webGivingActionResponse.forgetMe;

	mainViewModel.setIsAccountDetailsOpen(false);
	mainViewModel.isAppLoading = true;
	try {
		result = yield context.dataService.forgetMe({ request:{}});
	} finally {
		mainViewModel.isAppLoading = false;
	}

	setNewCRSFoken(result.NewCSRFToken);

	// clear preconfigured payment - Requirement A13
	if (viewData.PreconfiguredPaymentDetails) {
		viewData.PreconfiguredPaymentDetails.EmailAddress = '';
		viewData.PreconfiguredPaymentDetails.FirstName = '';
		viewData.PreconfiguredPaymentDetails.LastName = '';
		viewData.PreconfiguredPaymentDetails.MobileNumber = '';
	}

	clearUserRemembered();

	// clear params and force reload if SkipGivePage
	if (mainViewModel.uiConfiguration.SkipGivePage) {
		clearPreconfiguredUrl();
		window.location.reload();
		return;
	}

	viewData.Person = null;
	viewData.ConfirmedMobileNumber = null;
	context.appState = defaultAppState(viewData, undefined);
	mainViewModel.openPage(new WebGivingPageViewModel.Give(context.appState.paymentState, viewData.MerchantInfo, mainViewModel.viewData.BrandingPackage));
	mainViewModel.currentPageViewModel.setPageShouldBeVisible();

	const notification = new NotificationViewModel(`You've successfully signed out.`, 10);
	mainViewModel.showNotification(notification);
}

function* extendedResponseTimeTimer(currentPageViewModel:  WebGivingPageViewModel.ConfirmGift | WebGivingPageViewModel.ConfirmMobileNumber) {
	try {
		yield delay(extendedResponseTime);
		currentPageViewModel.setResponseTimeNotice(true);
	} finally {
		// If this fails we don't want to do anything
	}
}

function* handleAppCardContent(context: IWebGivingSagaContext) {
	const { dataService, appState } = context;
	try {
		const result = yield dataService.getAppContentCards({ request:{numCards: 2}});
		appState.appContentCards = result.Data;
	} catch {
		// If this fails we don't want to notify the user
	}
}

function* handleUpdateFastModeEnabled(context: IWebGivingSagaContext, mode: boolean): IterableIterator<any> {
	const { dataService, mainViewModel } = context;
	const currentModel = mainViewModel.currentPageViewModel;

	if (currentModel.type !== WebGivingPage.PaymentSuccess) {
		return;
	}

	try {
		const result: typeof webGivingActionResponse.updateFastModeEnabled = yield dataService.updateFastModeEnabled({
			request: {
				request: {
					FastModeEnabled: mode,
					GiftAmount: currentModel.data.giftAmount,
				},
			},
		});

		if (result.Success) {
			currentModel.data.fastModeCurrentlyEnabled = mode;
			currentModel.data.fastModeDailyLimit = result.FastModeLimit;
			currentModel.setFastMode(mode);
		} else if (result.ErrorCode && result.ErrorCode === UpdateFastModeEnabledErrorCode.SessionEnded) {
			const notificationViewModel = new NotificationViewModel('Your session has expired. Please refresh to re-authenticate.', 10,
			false, undefined, true);
			mainViewModel.showNotification(notificationViewModel);
			currentModel.setFastModeLoading(false);
		}
	} catch (e) {
		currentModel.setFastModeLoading(false);
	}
}

function isRecurringPayment(giftType: GiftType): boolean {
	return giftType === GiftType.Recurring;
}

function isOneTimePayment(giftType: GiftType): boolean {
	return giftType === GiftType.OneTime;
}

function displayApplePay(context: IWebGivingSagaContext): boolean {
	const { appState: { paymentState }, mainViewModel: { applePayAvailable } } = context;
	const afterToday = isAfterToday((paymentState as IWebGivingOneTimePaymentState).giftDate);
	const hasSplitItems = paymentState.LineItems && paymentState.LineItems.length > 1;
	if (!applePayAvailable) {
		return false;
	}

	// if it's a scheduled payment (recurring or OTFG)
	if (!isOneTimePayment(paymentState.giftType) || (isOneTimePayment(paymentState.giftType) && afterToday)) {
		// split are supported only if "recurring for splits" is enabled
		if (hasSplitItems) {
			return context.mainViewModel.viewData.MerchantInfo.ApplePayRecurringEnabledInSplitGiving;
		}

		return context.mainViewModel.viewData.MerchantInfo.SupportsApplePayRecurring;
	}

	return true;
}

function clearPreconfiguredUrl() {
	const query = window.location.search.substring(1);
	if (query.length) {
		if (window.history && window.history.pushState) {
			window.history.pushState({}, document.title, window.location.pathname);
		}
	}
}

function enableRecurringSuggestion(recurringSuggestionQueryResult: RecurringSuggestionQueryResult | null)
	: RecurringSuggestionQueryResult | null {
	if (recurringSuggestionQueryResult) {
		return recurringSuggestionQueryResult;
	}
	return null;
}

function getRedirectUrl(preconfiguredPaymentDetails: WebGivingPreconfiguredPayment | null, paymentToken: string): string | null | undefined {
	if (preconfiguredPaymentDetails && preconfiguredPaymentDetails.RedirectUrl) {
		var url = `https://${preconfiguredPaymentDetails.RedirectUrl}?paymentToken=${encodeURIComponent(paymentToken)}`;
		if (preconfiguredPaymentDetails.ExternalReference) {
			url += `&sr=${encodeURIComponent(preconfiguredPaymentDetails.ExternalReference)}`;
		}
		return url;
	}
	return null;
}

function updateMerchantInfo(context: IWebGivingSagaContext, response: MerchantConflictResponse | null | undefined) {
	if (!response) {
		return;
	}

	context.mainViewModel.viewData.MerchantInfo = response.MerchantInfo;
	context.mainViewModel.theme = response.Theme;
	clearInvalidCustomFields(response.MerchantInfo, context.appState.paymentState);

	if (!response.MerchantInfo.SupportsPayerCoversFees) {
		// ensure to update the view if merchant disables payer cover fees
		context.appState.paymentState.payerElectedToCoverFees = false;

		const currentModel = context.mainViewModel.currentPageViewModel;
		if (currentModel.type === WebGivingPage.ConfirmGift) {
			currentModel.merchantSupportsPayerCoversFees = false;
			currentModel.payerElectedToCoverFees.updateValue(false);
		}
	}
}

function updatePersonPaymentMethods(context: IWebGivingSagaContext, paymentMethods: WebGivingPaymentMethodModel[] | null | undefined) {
	if (!paymentMethods) {
		return;
	}

	if (context.appState.person) {
		context.appState.person.PaymentMethods = paymentMethods;
	}
}

function getConfirmPaymentResponseStatus(response: ConfirmPaymentResponse) {
	if (!response.ConfirmPaymentLineItemResults || !response.ConfirmPaymentLineItemResults.length) {
		return response.Status;
	}

	const { ConfirmPaymentLineItemResults } = response;
	const firstResponseStatus = ConfirmPaymentLineItemResults[0].Status;

	if (ConfirmPaymentLineItemResults.length > 1) {
		const allLineItemsHaveSameStatus = ConfirmPaymentLineItemResults.every(({ Status }) => Status === firstResponseStatus);

		if (allLineItemsHaveSameStatus && firstResponseStatus !== WebGivingPaymentResultStatus.Poll) {
			return firstResponseStatus;
		}

		const anyLineItemHasSuccessStatus = ConfirmPaymentLineItemResults.some(({ Status }) => splitLineItemSuccessStatuses.includes(Status));
		if (anyLineItemHasSuccessStatus) {
			return WebGivingPaymentResultStatus.Success;
		}

		return WebGivingPaymentResultStatus.Fail;
	}

	return firstResponseStatus;
}
